import { useState, useEffect, useContext, useCallback } from "react";
import {
  Box,
  Flex,
  Button,
  Center,
  Divider,
  Text,
  Link,
  useColorModeValue,
  Switch,
  Icon,
} from "@chakra-ui/react";
import moment from "moment";
import { Page } from "components/page";
import { CustomDatePicker } from "components/custom-datepicker";
import { AskAuth } from "components/ask-auth";
import { getSession, useSession } from "next-auth/react";
import toast from "react-hot-toast";
import {
  MostResults,
  TweetPerformance,
  TeamPerformance,
  BigChangeableGraph,
  SeePostSlider,
  SquaresPanel,
} from "components/analytics";
import { TweetContext } from "context/tweetContext";
import { firebaseClient, getToken } from "firebaseClient";
import { getAccount, getAccountId, updateUser } from "utils/sessionHelper";
import ProtectedRoute from "components/protected-route";
import * as analytics from "utils/analytics";
import { useFirebaseUser } from "../utils/useFirebaseUser";
import { MainContext } from "../context/mainContext";
import {
  getAccountStats,
  getStatsBetween,
  getTweetsStats,
} from "controllers/stats";
import { ProfileSelector } from "components/analytics/profile-selector";
import { useStats } from "hooks/useStats";
import {
  createSeries,
  readyOrgStats,
  removeResharedPosts,
} from "components/analytics/utils";
import { StatScreenshot } from "components/popups/stats-screenshot";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { BsShare } from "react-icons/bs";
import { textStyle, variant } from "theme/names";

export default function Stats({ teamStats = [] }) {
  const [refresh, refresher] = useState({});
  const [timeFrame, setTimeFrame] = useState(
    new Date(moment().subtract(30, "days").startOf("day").toString())
  ) as any;
  const [prevTimeFrame, setPrevTimeFrame] = useState({
    start: new Date(moment().subtract(14, "days").startOf("day").toString()),
    end: new Date(moment().subtract(30, "days").startOf("day").toString()),
  });
  const isAuthenticated = useFirebaseUser();
  const [isPublicStats, setIsPublicStats] = useState<boolean>(false);
  const mainContext: any = useContext(MainContext);
  const tweetContext: any = useContext(TweetContext);
  const [hasFetched, setHasFetched] = useState<boolean>(false);
  const [tweetStats, setTweetStats] = useState<any>([]);
  const [prevPeriodTweetStats, setPrevPeriodTweetStats] = useState<any>([]);
  const [accountStats, setAccountStats] = useState<any>([]);
  const [prevPeriodAccountStats, setPrevPeriodAccountStats] = useState<any>([]);
  const [orgStats, setOrgStats] = useState<any>([]);
  const [prevOrgStats, setPrevOrgStats] = useState<any>([]);
  const [mode, setMode] = useState<any>("personal");
  const [isLoadingTweetStats, setIsLoadingTweetStats] =
    useState<boolean>(false);
  const [isLoadingAccountStats, setIsLoadingAccountStats] =
    useState<boolean>(false);
  const [previousDayStat, setPreviousDayStat] = useState();
  const [dateSeries, setDateSeries] = useState(() => createSeries(timeFrame));
  const { data: session, status } = useSession() ?? {};
  const loading = status === "loading";

  const { updateStatsCounter } = useStats();
  const subHeadingTextColor = useColorModeValue("gray.700", "gray.500");
  const [statsForAccount, setStatsForAccount] = useState<any>(null);
  const [dateTypeSelection, setDateTypeSelection] = useState<any>("30");
  const [bigGraphStats, setBigGraphStats] = useState({
    type: "Followers",
    stats: [],
    labelValue: 0,
    percentageValue: 0,
    previousDayStat,
  });
  //@ts-ignore
  const [postsForDay, setPostsForDay] = useState(
    timeFrame?.start ? timeFrame.start : timeFrame
  );
  const [showSeePostSlider, setShowSeePostSlider] = useState<boolean>(false);
  const [forAccountUsername, setForAccountUsername] = useState("");
  const [screenshotData, setScreenshotData] = useState();
  const [showScreenshotPopup, setShowScreenshotPopup] = useState(false);
  const [screenshotType, setScreenshotType] = useState("stats_box_graph");

  const fetchOrgStats = useCallback(async () => {
    console.log("fetchOrgStats");
    setIsLoadingAccountStats(true);
    setIsLoadingTweetStats(true);
    setAccountStats([]);
    setPrevPeriodAccountStats([]);
    setTweetStats([]);
    setPrevPeriodTweetStats([]);
    setOrgStats([]);
    setPrevOrgStats([]);

    let data = {
      idUser: getAccount(session).id,
      timeFrame,
      tokenUserId: session?.user?.uid,
      token: await getToken(session, "scheduler"),
    };

    let prevPeriodData = {
      idUser: getAccount(session).id,
      timeFrame: prevTimeFrame,
      tokenUserId: session?.user?.uid,
      token: await getToken(session, "scheduler"),
    };

    const res = await fetch("/api/orgStats", {
      method: "POST",
      headers: new Headers({ "Content-Type": "application/json", token: "" }),
      credentials: "same-origin",
      body: JSON.stringify(data),
    });

    const resPrevPeriod = await fetch("/api/orgStats", {
      method: "POST",
      headers: new Headers({ "Content-Type": "application/json", token: "" }),
      credentials: "same-origin",
      body: JSON.stringify(prevPeriodData),
    });

    let json = await res.json();

    let jsonPrev = await resPrevPeriod.json();

    json.accountStats = await readyOrgStats([...json.accountStats], timeFrame);

    jsonPrev.accountStats = await readyOrgStats(
      [...jsonPrev.accountStats],
      prevTimeFrame
    );

    if (json?.success && jsonPrev?.success) {
      json.tweetStats.map((x) => {
        x.created_at = {
          ...x.created_at,
          nanoseconds: x.created_at._nanoseconds,
          seconds: x.created_at._seconds,
          toDate: () => {
            return new Date(x.created_at._nanoseconds);
          },
        };
      });
      json.accountStats.map((x) => {
        x.dateFetched = {
          ...x.dateFetched,
          toDate: () => {
            return new Date(x._nanoseconds);
          },
        };
      });
      jsonPrev.tweetStats.map((x) => {
        x.created_at = {
          ...x.created_at,
          nanoseconds: x.created_at._nanoseconds,
          seconds: x.created_at._seconds,
          toDate: () => {
            return new Date(x.created_at._nanoseconds);
          },
        };
      });
      jsonPrev.accountStats.map((x) => {
        x.dateFetched = {
          ...x.dateFetched,
          toDate: () => {
            return new Date(x._nanoseconds);
          },
        };
      });

      setTweetStats([...json.tweetStats]);
      setAccountStats([...json.accountStats]);
      setOrgStats([...json.orgStats]);
      setPrevPeriodTweetStats([...jsonPrev.tweetStats]);
      setPrevPeriodAccountStats([...jsonPrev.accountStats]);
      setPrevOrgStats([...jsonPrev.orgStats]);
    } else {
      toast.error("An error occured when fetching organization stats");
    }

    setIsLoadingAccountStats(false);
    setIsLoadingTweetStats(false);
  }, [mode, statsForAccount, timeFrame, prevTimeFrame]);

  const handleDateChange = async (dateSelectionObj) => {
    // when date picker is used
    setDateSeries([...createSeries(dateSelectionObj.value)]);

    if (dateSelectionObj?.value?.start) {
      const diffBetweenAB =
        moment(dateSelectionObj.value.end).diff(
          moment(dateSelectionObj.value.start),
          "days"
        ) + 1;
      const a = new Date(
        moment(dateSelectionObj.value.start)
          .subtract(diffBetweenAB, "day")
          .startOf("day")
          .toString()
      );
      const b = new Date(
        moment(dateSelectionObj.value.end)
          .subtract(diffBetweenAB, "day")
          .endOf("day")
          .toString()
      );
      //@ts-ignore
      setPrevTimeFrame({ start: a, end: b });
    } else {
      //default
      const diffNum = Number(
        dateSelectionObj?.type != "all"
          ? dateSelectionObj?.type != "customDate"
            ? dateSelectionObj?.type || 30
            : 30
          : moment(new Date())
              ?.startOf("day")
              .diff(dateSelectionObj.value, "days") || 30
      );
      const a = new Date(
        moment(dateSelectionObj.value)
          .subtract(diffNum, "day")
          .startOf("day")
          .toString()
      );
      const b = new Date(
        moment(a)
          .add(diffNum - 1, "day")
          .endOf("day")
          .toString()
      );
      // console.log(diffNum, { start: a, end: b }, 'prevtimeframe check');
      //@ts-ignore
      setPrevTimeFrame({ start: a, end: b });
    }

    setTimeFrame(dateSelectionObj.value);
    setDateTypeSelection(dateSelectionObj?.type);
  };

  const fetchAccountStats = async () => {
    try {
      setIsLoadingAccountStats(true);
      let stats = await getAccountStats(statsForAccount, timeFrame);
      setAccountStats([...stats]);
      let prevStats = await getAccountStats(statsForAccount, prevTimeFrame);
      setPrevPeriodAccountStats([...prevStats]);
    } catch (err) {
      console.log("Error in fetching account stats: ", err);
      toast.error("Error in fetching account stats: ", err);
    } finally {
      setIsLoadingAccountStats(false);
    }
  };

  const fetchPreviousDayStat = async () => {
    // @ts-ignore
    if (!timeFrame?.start) {
      const dayBeforeTimeframe = new Date(
        // @ts-ignore
        moment(timeFrame).subtract(1, "d").startOf("day")
      );
      const data = await getStatsBetween(
        statsForAccount,
        dayBeforeTimeframe,
        timeFrame
      );

      setPreviousDayStat(data);
    } else {
      const dayBeforeTimeframe = new Date(
        // @ts-ignore
        moment(timeFrame.start).subtract(1, "d").startOf("day")
      );
      // @ts-ignore
      const data = await getStatsBetween(
        statsForAccount,
        dayBeforeTimeframe,
        timeFrame.start
      );

      setPreviousDayStat(data);
    }
  };

  const fetchTweetStats = async () => {
    try {
      setIsLoadingTweetStats(true);
      let stats = await getTweetsStats(statsForAccount, timeFrame);
      stats = await removeResharedPosts([...stats]);
      setTweetStats([...stats]);
      let prevStats = await getTweetsStats(statsForAccount, prevTimeFrame);
      prevStats = await removeResharedPosts([...prevStats]);
      setPrevPeriodTweetStats([...prevStats]);
    } catch (err) {
      console.log("Error in fetching tweets stats: ", err);
      toast.error("Error in fetching tweets stats: ", err);
    } finally {
      setIsLoadingTweetStats(false);
    }
  };

  const fetchHistory = async () => {
    
    let url = `https://us-central1-ez4cast.cloudfunctions.net/tweetAnalytics-fetchAccountHistory?idAccount=${statsForAccount.idAccount}&idUser=${statsForAccount.id}`;
    let response = await fetch(url, { method: "GET" });
    await updateUser(session, { hasAnalytics: true });
    refresher({});
  };

  const refreshDate = (timeFrame, type) => {
    if (timeFrame?.start) {
      handleDateChange({ value: { ...timeFrame }, type });
    } else {
      handleDateChange({ value: timeFrame, type });
    }
  };

  const refreshAndFetchStats = async () => {
    try {
      setIsLoadingTweetStats(true);
      setIsLoadingAccountStats(true);
      fetchAccountStats();
      fetchTweetStats();
      fetchPreviousDayStat();
    } catch (err) {
      console.log("Error in refreshing stats: ", err);
      toast.error("Error in refreshing stats: ", err);
    }
  };

  const onProfileSelection = (selected, event) => {
    let psuedoMergedAccountUserDetails = {
      ...selected?.account,
      account: selected?.account,
    };

    setIsLoadingTweetStats(true);
    setIsLoadingAccountStats(true);

    selected?.mode != undefined ? setMode(selected?.mode) : setMode("personal");
    setStatsForAccount({ ...psuedoMergedAccountUserDetails });
    setForAccountUsername(selected?.account?.name);
    //console.log(selected?.account?.username, 'org username bug')
  };

  const onStatsBoxClick = (statsBoxObj) => {
    //BigGrapher change on statsbox click and statsbox load.
    setBigGraphStats((prevStats) => {
      if (prevStats.type !== statsBoxObj.type) {
        switch (statsBoxObj.type) {
          case 'Followers':
            analytics.log('follower_trend_viewed');
            break;
          case 'Likes':
          case 'Comments':
          case 'Impressions':
          case 'Shares':
            analytics.log('post_engagement_viewed', {
              postEngagementType: statsBoxObj.type,
            });
            break;
          default:
            break;
        }
      }
      return { ...statsBoxObj };
    });
  };

  const onHintButtonClick = (date) => {
    setShowSeePostSlider(true);
    setPostsForDay(date);
    //@ts-ignore
    tweetContext.close();
  };

  const takeScreenshot = (screenshotData) => {
    setScreenshotData(screenshotData?.data);
    setScreenshotType(screenshotData?.screenshotType);
    setShowSeePostSlider(false);
    setShowScreenshotPopup(true);
  };

  const onCloseSnapshopPopup = () => {
    setShowScreenshotPopup(false);
  };

  // useEffect(() => {
  //   if (isAuthenticated && session && statsForAccount?.idLinkedin) {
  //     (mode == "personal")
  //     ? refreshAndFetchStats()
  //     : fetchOrgStats();
  //   }
  // }, [timeFrame, refresh, mode]);

  useEffect(() => {
    if (
      isAuthenticated &&
      session &&
      !hasFetched &&
      statsForAccount?.idLinkedin
    ) {
      setHasFetched(true);
      refreshDate(timeFrame, dateTypeSelection);
      setStatsForAccount({ ...getAccount(session) });
      setForAccountUsername(getAccount(session)?.name);
      refreshAndFetchStats();
      updateStatsCounter("access_analytics");
      setIsPublicStats(getAccount(session)?.isPublicStats);
      // Add analytics event for viewing analytics
      analytics.log("analytics_viewed");
    }
  }, [session, isAuthenticated]);

  useEffect(() => {
    if (
      isAuthenticated &&
      session &&
      statsForAccount &&
      statsForAccount?.idLinkedin
    ) {
      mode == "personal" ? refreshAndFetchStats() : fetchOrgStats();
    }
  }, [statsForAccount, timeFrame, mode, refresh, dateTypeSelection]);

  if (!loading && !session) {
    return <ProtectedRoute />;
  }

  if (session && isAuthenticated && !statsForAccount) {
    setIsLoadingTweetStats(true);
    setIsLoadingAccountStats(true);
    setStatsForAccount(getAccount(session));
    setForAccountUsername(getAccount(session)?.name);
    refreshDate(timeFrame, dateTypeSelection);
  }

  // console.log(tweetStats);
  //console.log(statsForAccount, "account");
  // console.log("!!account " + !!account);
  // console.log("getAccount(session)?.thWriteAccessToken " + getAccount(session)?.thWriteAccessToken);
  // console.log("getAccount(session)?.thApp " + !!getAccount(session)?.thApp);
  // console.log("timeframe and prevTimeframe", timeFrame, prevTimeFrame);

  return (
    <Page
      title="My Stats"
      contentBesidesTitle={
        <Flex
          direction={{ base: "column-reverse", lg: "row" }}
          mt={{ base: "1" }}
          alignItems="center"
        >
          {getAccount(session)?.isPublicStats && (
            <CopyToClipboard
              text={`https://taplio.com/stats?idUser=${getAccount(
                session
              )?.id?.replace("auth0|", "")}`}
              onCopy={() => {
                
                toast.success("Public link copied to clipboard");
              }}
            >
              <Button
                ml={{ base: "5", md: "10" }}
                mt={{ base: "2", md: "0" }}
                variant={variant.Button.primary}
                // size="sm"
                leftIcon={<BsShare />}
                // border="2px"
                // borderColor="taplio.500"
                // borderRadius="xl"
                // paddingX="4"
              >
                Copy public link
              </Button>
            </CopyToClipboard>
          )}
          <Flex ml={5} mb={0} alignItems="center">
            <Text textStyle="body.medium.light">Share stats publicly</Text>
            <Switch
              ml={3}
              mt="1"
              isChecked={isPublicStats}
              onChange={async () => {
                setIsPublicStats(!isPublicStats);
                await updateUser(
                  session,
                  { isPublicStats: !isPublicStats },
                  true,
                  true
                );
              }}
            />
          </Flex>
        </Flex>
      }
    >
      <Flex justifyContent="space-between">
        <Flex>
          {/* <Heading variant={variant.Heading.pageTitle} >My Stats</Heading> */}
          {
            // session?.user?.data?.isAdmin && (
            // <Flex
            //   direction={{ base: "column-reverse", lg: "row" }}
            //   mt={{ base: "1" }}
            //   alignItems="center"
            // >
            //   {getAccount(session)?.isPublicStats && (
            //     <CopyToClipboard
            //       text={`https://taplio.com/stats?idUser=${getAccount(
            //         session
            //       )?.id?.replace("auth0|", "")}`}
            //       onCopy={() => {
            //         
            //         toast.success("Public link copied to clipboard");
            //       }}
            //     >
            //       <Button
            //         ml={{ base: "5", md: "10" }}
            //         mt={{ base: "2", md: "0" }}
            //         variant={variant.Button.primary}
            //         // size="sm"
            //         leftIcon={<BsShare />}
            //         // border="2px"
            //         // borderColor="taplio.500"
            //         // borderRadius="xl"
            //         // paddingX="4"
            //       >
            //         Copy public link
            //       </Button>
            //     </CopyToClipboard>
            //   )}
            //   <Flex ml={5} mb={0} alignItems="center">
            //     <Text textStyle="body.medium.light">
            //       Share stats publicly
            //     </Text>
            //     <Switch
            //       ml={3}
            //       mt="1"
            //       isChecked={isPublicStats}
            //       onChange={async () => {
            //         setIsPublicStats(!isPublicStats);
            //         await updateUser(
            //           session,
            //           { isPublicStats: !isPublicStats },
            //           true,
            //           true
            //         );
            //       }}
            //     />
            //   </Flex>
            // </Flex>
            // )
          }
        </Flex>
      </Flex>
      <Flex mt={5} justifyContent="space-between">
        <Text
          {...{
            textStyle: "body.medium.light",
          }}
        >
          {!getAccount(session)?.idLinkedin && (
            <>
              To collect your statistics, please install the{" "}
              <Link
                textDecoration="underline"
                isExternal
                href="https://chrome.google.com/webstore/detail/taplio-stats/dfpbcakpogbfaohnnjlgghdjkgaoiaik"
                {...{
                  color: "primary.lightMode.default",
                  _dark: {
                    color: "primary.darkMode.default",
                  },
                }}
              >
                Taplio Chrome Extension
              </Link>
              .
            </>
          )}{" "}
          Your stats will then be refreshed automatically every time you visit
          your profile (max once a day).
        </Text>
        {/* <Box position="relative" mt="-5px">
          <Button
            size="sm"
            colorScheme="linkedin"
            bg="blue.700"
            _hover={{ textDecoration: "none", bg: "blue.600" }}
            // variant="outline"
            minW="90px"
            color="white"
            as={Link}
            isExternal href="https://linkedin.com"
            leftIcon={<VscRefresh />}
            p={0}
            pl={5}
            pr={5}
            m={0}
          >
            Refresh
          </Button>
        </Box> */}
      </Flex>
      <Flex
        height="fit-content"
        justifyContent="end"
        flexWrap={"wrap-reverse"}
        gap={4}
        position="sticky"
        top="10"
        mt="8"
        zIndex="2"
      >
        {session && (
          <ProfileSelector
            session={session}
            onSelectedCallBack={(selectedMember, e) => {
              onProfileSelection(selectedMember, e);
            }}
          />
        )}
        <Box flex={1} />
        {/* // keep content together when wraping */}
        <Flex flexWrap={"wrap"} gap="4" justifyContent={"flex-end"}>
          <CustomDatePicker
            onChange={handleDateChange}
            returnDefaultSelection={true}
            showAlldPicker={true}
          />
          <Button
            ml={4}
            variant={variant.Button.primary}
            leftIcon={
              <Icon viewBox="0 0 16 15">
                <svg
                  width="16"
                  height="15"
                  viewBox="0 0 16 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M14.1875 8.53125C13.7812 8.40625 13.375 8.625 13.25 9.03125C12.5938 11.375 10.4062 13 7.96875 13C5.875 13 4 11.8125 3.0625 10H6.21875C6.625 10 6.96875 9.6875 6.96875 9.25C6.96875 8.84375 6.65625 8.5 6.21875 8.5H1.21875C0.8125 8.5 0.46875 8.84375 0.46875 9.25V14.25C0.46875 14.6875 0.8125 15 1.21875 15C1.65625 15 1.96875 14.6875 1.96875 14.25V11.0625C3.21875 13.1562 5.5 14.5 7.96875 14.5C11.0625 14.5 13.8125 12.4375 14.6875 9.46875C14.8125 9.0625 14.5938 8.65625 14.1875 8.53125ZM14.75 0C14.3125 0 14 0.34375 14 0.75V3.96875C12.75 1.875 10.4688 0.5 8 0.5C4.90625 0.5 2.125 2.59375 1.25 5.5625C1.15625 5.9375 1.375 6.375 1.78125 6.46875C2.1875 6.59375 2.59375 6.375 2.6875 5.96875C3.375 3.65625 5.5625 2 8 2C10.0625 2 11.9375 3.21875 12.875 5H9.75C9.3125 5 9 5.34375 9 5.75C9 6.1875 9.3125 6.5 9.75 6.5H14.75C15.1562 6.5 15.5 6.1875 15.5 5.75V0.75C15.5 0.34375 15.1562 0 14.75 0Z"
                    fill="currentColor"
                  />
                </svg>
              </Icon>
            }
            onClick={() => {
              handleDateChange({ value: timeFrame, type: "customDate" });
              refreshAndFetchStats();
            }}
          >
            Refresh
          </Button>
        </Flex>
      </Flex>

      {/* { false ? ( */}
      {statsForAccount?.idLinkedin ? (
        <Box>
          <SquaresPanel
            tweetStats={tweetStats}
            prevPeriodTweetStats={prevPeriodTweetStats}
            accountStats={accountStats}
            prevPeriodAccountStats={prevPeriodAccountStats}
            timeFrame={timeFrame}
            prevTimeFrame={prevTimeFrame}
            previousDayStat={previousDayStat}
            isLoadingAccountStats={isLoadingTweetStats}
            isLoadingTweetStats={isLoadingTweetStats}
            mode={mode}
            onClickCallback={onStatsBoxClick}
            takeScreenshot={takeScreenshot}
          />
          <Center w={"100%"} maxW={1200}>
            <Divider width="40" borderBottomWidth="2px" mb="50px" mt="50px" />
          </Center>

          <BigChangeableGraph
            isLoading={isLoadingTweetStats || isLoadingAccountStats}
            stats={bigGraphStats?.stats}
            bigGraphOf={bigGraphStats?.type}
            bigGraphTotalCounts={bigGraphStats?.labelValue}
            previousDayStat={{
              prevPeriodAccountStats,
              prevPeriodTweetStats,
              prevOrgStats,
              timeFrame,
              prevTimeFrame,
            }}
            hintButtonOnClick={onHintButtonClick}
            canScreenshot={true}
            takeScreenshot={takeScreenshot}
          />
          <Center w={"100%"} maxW={1200}>
            <Divider width="40" borderBottomWidth="2px" mb="50px" mt="50px" />
          </Center>
          {mode == "organization" && (
            <Box mt={10} mb={10}>
              <TeamPerformance
                stats={orgStats}
                isLoading={isLoadingTweetStats}
              />
              <Center w={"100%"} maxW={1200} mt={10}>
                <Divider width="40" borderBottomWidth="2px" mb="10" />
              </Center>
            </Box>
          )}
          <Box>
            <MostResults stats={tweetStats} isLoading={isLoadingTweetStats} />
          </Box>
          <Center w={"100%"} maxW={1200}>
            <Divider width="40" borderBottomWidth="2px" marginY="10" />
          </Center>
          {/* {
              accountStats?.length >= 2 && (
                <Box>
                  <FollowerGain
                    stats={accountStats}
                    isLoading={isLoadingAccountStats}
                    previousDayStat={previousDayStat}
                    dateSeries={dateSeries}
                  />
                  <Center>
                    <Divider width="40" borderBottomWidth="2px" marginY="10" />
                  </Center>
                </Box>
              )
            } */}
          <Box>
            <TweetPerformance
              stats={tweetStats}
              isLoading={isLoadingTweetStats}
            />
          </Box>
        </Box>
      ) : (
        <AskAuth />
      )}
      <SeePostSlider
        forDate={postsForDay}
        linkedInPosts={tweetStats}
        loading={isLoadingTweetStats}
        openSidebar={showSeePostSlider}
        setOpenSidebar={setShowSeePostSlider}
        mode={mode}
      />
      <StatScreenshot
        isOpen={showScreenshotPopup}
        onClose={onCloseSnapshopPopup}
        screenshotOf={screenshotType}
        data={screenshotData}
        forAccount={forAccountUsername}
        previousDayStat={prevPeriodAccountStats}
      />
    </Page>
  );
}
